import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

import Header from "./header";
import ServingInfo from "./serving-info";
import CalorieInfo from "./calorie-info";
import FactItemList from "./fact-item-list";
import Footnotes from "./footnotes";
import Ingredients from "./ingredients";
import AllergyInfo from "./allergy-info";
import HealthClaims from "./health-claims";

import { footnoteTemplates, noteMarkList } from "../lib/constants";
import { getPicklist } from "../lib/data";
import { useNutritionPanels } from "../hooks/use-nutrution-panels";
import { useTemplateVersion } from "../hooks/use-template-version";
import NotificationBar from "../../../../notification-bar";

const NewPanel = ({ isNutrition, isQaEdit, ...props }) => {
    const [allergenOptions, setAllergenOptions] = useState([]);
    const [healthClaimOptions, setHealthClaimOptions] = useState([]);
    const { isNewTemplateUsed } = useTemplateVersion();
    const {
        addFact,
        updateFacts,
        updateField,
        updateFieldDeep,
        removePanel,
        removeColumn,
        addColumn,
        handleResetDefaultNutritionValues: onResetDefaultNutritionValues,
        handleResetDefaultVitaminsValues: onResetDefaultVitaminsValues,
        globalDqErrors,
    } = useNutritionPanels();

    useEffect(() => {
        async function fetchData() {
            const allergens = await getPicklist("Allergens");
            const healthClaims = await getPicklist("HealthClaims");

            if (!allergens || !healthClaims) {
                return;
            }

            setAllergenOptions(allergens);
            setHealthClaimOptions(healthClaims);
        }

        fetchData();
    }, []);

    const columnCount = props.panel.columns.length;
    const buttonsRemovePrepared =
        columnCount > 1
            ? Array(columnCount)
                  .fill()
                  .map((a, index) => {
                      return (
                          <button
                              key={`asPrepared_${index}of${columnCount}`}
                              className="button -small -right"
                              onClick={() => {
                                  removeColumn(index);
                              }}
                          >
                              {index + 1}
                          </button>
                      );
                  })
            : null;

    const servingInfoData = props.panel.columns.map((c) => {
        return {
            header: c.header,
            servingSize: c.servingSize,
            servingSizeMetric: c.servingSizeMetric,
            servingSizeVisual: c.servingSizeVisual,
            servingsPerContainer: c.servingsPerContainer,
            servingAccuracy: c.servingAccuracy,
            servingSizeDescription: c.servingSizeDescription,
        };
    });

    const calorieInfoData = props.panel.columns.reduce((col, c) => {
        col.push(c.calorie);

        return col;
    }, []);

    const dietaryInfoData = props.panel.columns.reduce((col, c) => {
        col.push({ facts: c.dietary.facts });

        return col;
    }, []);

    const vitamineralData = props.panel.columns.reduce((col, c) => {
        col.push({ facts: c.vitamineral.facts });

        return col;
    }, []);

    return (
        <div className="nutrition-panel panel">
            {globalDqErrors && globalDqErrors.length > 0 && (
                <div className="column -span12">
                    <NotificationBar type="warning">
                        <div>
                            {globalDqErrors.map((error) => (
                                <div>{error}</div>
                            ))}
                        </div>
                    </NotificationBar>
                </div>
            )}
            <div className="button-group">
                <h2>
                    {props.name}{" "}
                    <button
                        className="button -text -small"
                        onClick={removePanel}
                    >
                        Delete Panel
                    </button>
                </h2>
                {buttonsRemovePrepared}
                <button
                    className="button -small -right"
                    onClick={addColumn}
                >
                    <FontAwesomeIcon icon={faPlus} /> Prepared
                </button>
            </div>

            <Header
                value={props.panel.header}
                updateField={updateField}
                isQaEdit={isQaEdit}
            />

            <ServingInfo
                data={servingInfoData}
                panel={props.selectedIndex}
                updateField={updateField}
                enableHorizontalScroller
            />

            <CalorieInfo
                data={calorieInfoData}
                panel={props.selectedIndex}
                updateField={updateField}
                section="calorie"
                enableHorizontalScroller
            />

            <FactItemList
                enableWarnings
                data={dietaryInfoData}
                panel={props.selectedIndex}
                columnCount={columnCount}
                addFact={addFact}
                updateFacts={updateFacts}
                noteMarkList={noteMarkList}
                isNutrition={isNutrition}
                section="dietary"
                isNewTemplateUsed={isNewTemplateUsed}
                onResetDefaultNutritionValues={onResetDefaultNutritionValues}
                enableHorizontalScroller
            />

            <FactItemList
                data={vitamineralData}
                panel={props.selectedIndex}
                columnCount={columnCount}
                addFact={addFact}
                updateFacts={updateFacts}
                // showDescription={props.showDescription}
                noteMarkList={noteMarkList}
                isNutrition={isNutrition}
                section="vitamineral"
                isNewTemplateUsed={isNewTemplateUsed}
                onResetDefaultVitaminsValues={onResetDefaultVitaminsValues}
                enableHorizontalScroller
            />

            <Footnotes
                data={props.panel.footnotes}
                presets={footnoteTemplates}
                noteMarkList={noteMarkList}
                updateField={updateField}
            />
            <Ingredients
                data={props.panel.ingredients}
                updateField={updateFieldDeep}
            />
            {props.panel.healthClaims && (
                <HealthClaims
                    data={props.panel.healthClaims}
                    allergenOptions={allergenOptions}
                    healthClaimOptions={healthClaimOptions}
                    allergens={props.panel.allergens}
                    updateField={updateField}
                />
            )}
            {props.panel.allergens && (
                <AllergyInfo
                    data={props.panel.allergens}
                    allergenOptions={allergenOptions}
                    updateField={updateField}
                />
            )}
        </div>
    );
};

export default NewPanel;
